import { createFeatureSelector, createSelector } from '@ngrx/store'
import {
  EVENT_TICKET_PACKAGES_FEATURE_KEY,
  EventTicketPackagesState,
  eventTicketPackagesAdapter,
  initialEventTicketPackagesState,
} from './event-ticket-packages.reducer'
import { isEmpty } from 'lodash'
import { GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'

const selectEventTicketPackagesStateBase = createFeatureSelector<EventTicketPackagesState>(
  EVENT_TICKET_PACKAGES_FEATURE_KEY,
)

export const selectEventTicketPackagesState = createSelector(
  selectEventTicketPackagesStateBase,
  (state) => state ?? initialEventTicketPackagesState,
)

const { selectAll, selectEntities } = eventTicketPackagesAdapter.getSelectors()

export const selectEventTicketPackagesLoaded = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state?.loaded,
)

export const selectAllEventTicketPackages = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => selectAll(state),
)

export const selectPaidEventTicketPackages = createSelector(selectAllEventTicketPackages, (ticketPackages) =>
  ticketPackages.filter((ticketPackage) => ticketPackage.price && Number(ticketPackage.price) > 0),
)

export const selectEventTicketPackagesEntities = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => selectEntities(state),
)

export const selectSelectedId = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state.selectedId,
)

export const selectEntity = createSelector(
  selectEventTicketPackagesEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
)

export const selectCount = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state?.count || 0,
)

export const selectCurrentFilters = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state.filters,
)

export const selectDisplayColumns = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state?.displayColumns || [],
)

export const selectDataView = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state?.dataView ?? GridSwitchEnum.Grid,
)

export const selectEditCreateLoaded = createSelector(
  selectEventTicketPackagesState,
  (state: EventTicketPackagesState) => state?.editCreateLoaded,
)

export const selectedEventTicketPackageIndexFromList = createSelector(
  selectAllEventTicketPackages,
  selectSelectedId,
  (allEventTicketPackages, selectedId) =>
    allEventTicketPackages.findIndex((ticketPackage) => ticketPackage.uuid === selectedId),
)

export const selectNextFilteredEventTicketPackageId = createSelector(
  selectAllEventTicketPackages,
  selectedEventTicketPackageIndexFromList,
  (allEventTicketPackages, selectedEventTicketPackageIndexFromList) =>
    selectedEventTicketPackageIndexFromList !== -1 &&
    selectedEventTicketPackageIndexFromList < allEventTicketPackages.length - 1
      ? allEventTicketPackages[selectedEventTicketPackageIndexFromList + 1].uuid
      : undefined,
)

export const selectNextEventTicketPackageId = createSelector(
  selectCurrentFilters,
  selectEntity,
  selectNextFilteredEventTicketPackageId,
  (currentFilters, selectedEventTicketPackage, nextFilteredEventTicketPackageId) =>
    !isEmpty(currentFilters) ? nextFilteredEventTicketPackageId : selectedEventTicketPackage?.next_id,
)

export const selectPrevFilteredEventTicketPackageId = createSelector(
  selectAllEventTicketPackages,
  selectedEventTicketPackageIndexFromList,
  (allEventTicketPackages, selectedEventTicketPackageIndexFromList) =>
    selectedEventTicketPackageIndexFromList > 0
      ? allEventTicketPackages[selectedEventTicketPackageIndexFromList - 1].uuid
      : undefined,
)

export const selectPrevEventTicketPackageId = createSelector(
  selectCurrentFilters,
  selectEntity,
  selectPrevFilteredEventTicketPackageId,
  (currentFilters, selectedEventTicketPackage, prevFilteredEventTicketPackageId) =>
    !isEmpty(currentFilters) ? prevFilteredEventTicketPackageId : selectedEventTicketPackage?.prev_id,
)
